import { Button, Icon, Modal } from '@aidkitorg/component-library';
import React, { useCallback } from 'react';
import { usePayments } from '../../../Contexts/PaymentsContext';
import { paymentTypeToString } from '../../../utils/payment_utils';
import * as XLSX from 'xlsx';
import { useLocalizedStrings } from '../../../Localization';
import { getTodayAsYYYYMMDD, toPascalCase } from '../../../utils/adminPanel.utils';

type PaymentBatchDownloadModalProps = {
  batchName: string,
  downloadModalOpen: boolean,
  setDownloadModalOpen: React.Dispatch<React.SetStateAction<boolean>>
}

export const PaymentBatchDownloadModal: React.FC<PaymentBatchDownloadModalProps> = ({
  batchName,
  downloadModalOpen,
  setDownloadModalOpen,
}) => {
  const L = useLocalizedStrings()

  const { getBatchRecipientDetails, getBatchName} = usePayments();
  
  const recipientDetails = getBatchRecipientDetails(batchName)

  const formattedBatchName = toPascalCase(getBatchName(batchName))
  const processedRecipientDetails = recipientDetails.map((recipient) => {
    let formattedPaymentRecipientDate = ''
    if (recipient.paymentInitiatedDate !== '') {
      const date = new Date(recipient.paymentInitiatedDate)
      formattedPaymentRecipientDate = String(new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }).format(date))
    }
    return {
      ...recipient,
      paymentMethod: paymentTypeToString(recipient.paymentMethod),
      paymentInitiatedDate: formattedPaymentRecipientDate
    }})

  const downloadPaymentBatchAsCSV = useCallback(() => {
    const convertToCSV = (objArray: any[]): string => {
      const array = [Object.keys(objArray[0])].concat(objArray.map(obj => Object.values(obj)));
      return array
        .map(row =>
          row.map(String).map(val => `"${val.replace(/"/g, '""')}"`).join(',')
        )
        .join('\n');
    };

    const csv = convertToCSV(processedRecipientDetails)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)

    const fileName = formattedBatchName + "_Recipients_" + getTodayAsYYYYMMDD() + ".csv"

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName)
    document.body.appendChild(link)
    link.click();
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }, [processedRecipientDetails])


  const downloadPaymentBatchAsXLSX = useCallback(() => {
    const worksheet = XLSX.utils.json_to_sheet(processedRecipientDetails);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');

    const fileName = formattedBatchName + "_Recipients_" + getTodayAsYYYYMMDD() + ".xlsx"

    XLSX.writeFile(workbook, fileName);
  }, [processedRecipientDetails])

  return (
    <Modal title={L.admin_panel.payments.export_recipients} open={downloadModalOpen} onClose={() => setDownloadModalOpen(false)}>
      <div className="pb-4">
        {L.admin_panel.payments.export_recipients_desc}
      </div>
      <div className="flex flex-col gap-y-2">
        <Button onClick={downloadPaymentBatchAsCSV} variant="secondary" className="!justify-start">
          <div className="flex gap-4 items-center py-2">
            <Icon name="ArrowDownTrayIcon" role='decorative'/>
            <div className="!text-left"> 
              <div>{L.admin_panel.payments.csv_format}</div>
              <div className="font-light">{L.admin_panel.payments.csv_format_desc}</div>
            </div>
          </div>
        </Button>
        <Button onClick={downloadPaymentBatchAsXLSX} variant="secondary" className="!justify-start">
          <div className="flex gap-4 items-center py-2">
            <Icon name="TableCellsIcon" role='decorative'/>
            <div className="!text-left"> 
              <div>{L.admin_panel.payments.xlsx_format}</div>
              <div className="font-light">{L.admin_panel.payments.xlsx_format_desc}</div>
            </div>
          </div>
        </Button>
      </div>
    </Modal>
  )
}